import type { ReactNode } from "react"
import React, { useEffect, useState } from "react"
import { CgClose } from "react-icons/cg"

import type { StackProps } from "@chakra-ui/react"
import {
  ButtonGroup,
  Flex,
  IconButton,
  Stack,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react"

import { CheckmarkIcon, Confirmation, valuesAreEqual } from "@supliful/common"

type InlineEditableProps<Value> = {
  initialValue: Value
  onSave: (value: Value) => void
  confirmationText?: (value: Value) => ReactNode
  confirmationTitle?: string
  renderInput: (value: Value, onChange: (value: Value) => void) => ReactNode
  wrapperProps?: Partial<StackProps>
  saveLabel?: string
}

export function InlineEditable<T>({
  initialValue,
  onSave,
  confirmationText,
  confirmationTitle,
  saveLabel,
  renderInput,
  wrapperProps,
}: InlineEditableProps<T>) {
  const [currentValue, setCurrentValue] = useState(initialValue)
  const hasEdits = !valuesAreEqual(currentValue, initialValue)
  const confirmationProps = useDisclosure()

  useEffect(() => {
    setCurrentValue(initialValue)
  }, [initialValue])

  const handleSave = () => {
    onSave(currentValue)
    confirmationProps.onClose()
  }

  const handleCancel = () => {
    setCurrentValue(initialValue)
    confirmationProps.onClose()
  }

  const input = renderInput(currentValue, setCurrentValue)
  const confirmation = confirmationText ? confirmationText(currentValue) : null

  return (
    <Stack
      direction="row"
      align="center"
      textAlign="left"
      fontSize="16"
      w="full"
      spacing={4}
      {...wrapperProps}
    >
      <Flex minH={8} align="center" flexGrow={2}>
        {input}
      </Flex>

      <ButtonGroup
        justifyContent="center"
        size="sm"
        visibility={hasEdits ? "visible" : "hidden"}
      >
        <Tooltip label={saveLabel || "Save changes"}>
          {confirmation ? (
            <Confirmation
              {...confirmationProps}
              title={confirmationTitle}
              text={confirmation}
              onSubmit={handleSave}
              onCancel={handleCancel}
            >
              <IconButton
                aria-label="Save"
                size="sm"
                colorScheme="primary"
                onClick={confirmationProps.onOpen}
                icon={<CheckmarkIcon />}
              />
            </Confirmation>
          ) : (
            <IconButton
              aria-label="Save"
              size="sm"
              colorScheme="primary"
              onClick={handleSave}
              icon={<CheckmarkIcon />}
            />
          )}
        </Tooltip>

        <Tooltip label="Reset changes">
          <IconButton
            aria-label="Cancel"
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={handleCancel}
            icon={<CgClose />}
          />
        </Tooltip>
      </ButtonGroup>
    </Stack>
  )
}
